import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const TeacherHomework = () => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [document, setDocument] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const classesRes = await api.get('/teacher/classes', { headers });
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);

      const subjectsRes = await api.get('/teacher/subjects', { headers });
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);

      const homeworkRes = await api.get('/teacher/homework', { headers });
      setHomeworks(Array.isArray(homeworkRes.data) ? homeworkRes.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedClass || !selectedSubject || !title || !dueDate) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('class_id', selectedClass);
      formData.append('subject_id', selectedSubject);
      formData.append('title', title);
      formData.append('description', description || '');
      formData.append('due_date', dueDate);
      if (document) {
        formData.append('document', document);
      }

      await api.post('/teacher/homework', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });

      alert('✅ Devoir publié !');

      setTitle('');
      setDescription('');
      setSelectedClass('');
      setSelectedSubject('');
      setDocument(null);
      setShowForm(false);

      fetchInitialData();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">📝 Planifier un devoir</h1>
          <p className="text-gray-500">Créez et consultez les devoirs de vos classes</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400]"
        >
          {showForm ? 'Annuler' : '+ Nouveau devoir'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="font-bold text-[#2C3E50] mb-4">📋 Nouveau devoir</h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Classe *</label>
                <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required>
                  <option value="">-- Choisir --</option>
                  {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Matière *</label>
                <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required>
                  <option value="">-- Choisir --</option>
                  {subjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Exercices chapitre 3" className="w-full px-3 py-2 border rounded-lg" required />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description / Consigne</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" placeholder="Détails du devoir..." className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date de remise *</label>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">📎 Document joint (optionnel)</label>
                <input
                  type="file"
                  onChange={(e) => setDocument(e.target.files[0])}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  PDF, Word, image ou texte • Max 10 MB
                </p>
                {document && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Fichier sélectionné : {document.name}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition disabled:opacity-50">
                {saving ? 'Publication...' : '📝 Publier le devoir'}
              </button>
            </div>
          </form>
        </div>
      )}

      {homeworks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">📭 Aucun devoir planifié</p>
          <p className="text-sm">Cliquez sur "Nouveau devoir" pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {homeworks.map((hw) => (
            <div key={hw.id} className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-[#2C3E50]">{hw.title}</h3>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">{hw.subject}</span>
              </div>
              <p className="text-sm text-gray-500 mb-2">{hw.class_name}</p>
              {hw.description && (<p className="text-sm text-gray-600 mb-3">{hw.description}</p>)}

              {hw.document_url && (
                <a
                  href={hw.document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 text-sm text-[#E67E22] hover:text-[#D35400] mb-3"
                >
                  <span>📎</span>
                  <span>Télécharger le document</span>
                </a>
              )}

              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>📅 À rendre le {hw.due_date}</span>
                <span>Publié le {hw.created_at}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherHomework;